import { useEffect, useState } from 'react';
import { SEO, BreadcrumbSchema, SpeakableSchema, FaqSchema } from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import { useInView } from 'react-intersection-observer';
import { AnimatePresence, motion } from 'framer-motion';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { CTABanner } from '@/components/CTABanner';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, MapPin, Shield, Award, Star, ArrowRight, Phone,
  Wrench, Hammer, Layers, Home, Ruler, Palette,
} from 'lucide-react';

/* Shared slideshow pool */
import slide1 from '@/assets/deckpro (1).jpeg';
import slide2 from '@/assets/deckpro (5).jpeg';
import slide3 from '@/assets/deckpro (7).jpeg';
import slide4 from '@/assets/deckpro (9).jpeg';
import slide5 from '@/assets/deckpro (11).jpeg';
import slide6 from '@/assets/deckpro (15).jpeg';
import slide7 from '@/assets/deckpro (18).jpeg';
import slide8 from '@/assets/deckpro (24).jpeg';
import slide9 from '@/assets/deckpro (26).jpeg';
import slide10 from '@/assets/deckpro (28).jpeg';
import slide11 from '@/assets/deckpro (31).jpeg';
import slide12 from '@/assets/deckpro (35).jpeg';
import slide13 from '@/assets/deckpro (38).jpeg';
import slide14 from '@/assets/deckpro (41).jpeg';

const slideshowImages = [slide1, slide2, slide3, slide4, slide5, slide6, slide7, slide8, slide9, slide10, slide11, slide12, slide13, slide14];

export interface CountyPageData {
  county: string;
  slug: string;
  hero: {
    headline: string;
    description: string;
  };
  seoBlock: {
    title: string;
    paragraphs: string[];
  };
  services: { title: string; desc: string; icon: React.ComponentType<{ className?: string }> }[];
  towns: string[];
  images: string[];
  imageAlts: string[];
  reasons: { icon: React.ComponentType<{ className?: string }>; title: string; desc: string }[];
  cta: {
    headline: string;
    description: string;
  };
}

function Anim({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={`transition-all duration-600 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

/* ─── Auto-rotating slideshow ──────────────────── */
function AutoSlideshow({ county }: { county: string }) {
  const images = slideshowImages;
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * images.length));

  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 3000);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <div className="relative">
      <div className="relative aspect-[4/5] border-4 border-navy shadow-hard overflow-hidden bg-navy">
        <AnimatePresence mode="wait">
          <motion.img
            key={idx}
            src={images[idx]}
            alt={`Deck project in ${county} by DeckPros NJ — photo ${idx + 1}`}
            className="w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          />
        </AnimatePresence>

        <div className="absolute top-3 left-3 bg-lime border-2 border-navy px-3 py-1.5 font-display font-bold text-[10px] uppercase tracking-[0.2em] text-navy flex items-center gap-1.5">
          <MapPin className="w-3 h-3" />
          {county}, NJ
        </div>

        <div className="absolute bottom-3 right-3 bg-navy border-2 border-lime text-lime px-2.5 py-1 font-display font-bold text-[10px] uppercase tracking-wider">
          {idx + 1} / {images.length}
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-1.5 mt-4">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`View image ${i + 1}`}
            className={`h-1.5 transition-all duration-300 ${
              i === idx ? 'w-8 bg-lime' : 'w-1.5 bg-navy/20 hover:bg-navy/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function CountyPage({ data }: { data: CountyPageData }) {
  const canonical = `/areas/${data.slug}`;

  const faqs = [
    {
      question: `How much does a new deck cost in ${data.county}?`,
      answer: `Deck costs in ${data.county} typically range from $15–25 per sq ft for pressure-treated wood, $30–45/sq ft for composite (Trex, TimberTech, Azek), and $50–80+/sq ft for premium hardwoods. A standard 300 sq ft deck is usually $6,000–$20,000 installed, depending on materials and features.`,
    },
    {
      question: `Do I need a permit to build a deck in ${data.county}?`,
      answer: `Yes — most deck projects in ${data.county} municipalities require a building permit, especially decks attached to the home or higher than 30 inches. DeckPros NJ handles all permits, HOA paperwork, and inspections as part of every project.`,
    },
    {
      question: `How long does a deck project take in ${data.county}?`,
      answer: `Most residential decks in ${data.county} take 3–7 days to build once construction begins. Permitting typically adds 1–2 weeks upfront. Complex multi-level or outdoor-living projects can take up to 2 weeks. Simple repairs can finish in 1–2 days.`,
    },
    {
      question: `What towns does DeckPros NJ serve in ${data.county}?`,
      answer: `DeckPros NJ serves ${data.towns.slice(0, 6).join(', ')}${data.towns.length > 6 ? ', and other towns across ' + data.county : ''}. Call (201) 657-0855 to confirm service in your exact town.`,
    },
    {
      question: `Is DeckPros NJ licensed and insured in ${data.county}?`,
      answer: `Yes. DeckPros NJ holds an active NJ Home Improvement Contractor license, is fully bonded, and carries general liability and workers compensation insurance. We are A+ rated with the Better Business Bureau.`,
    },
  ];

  const countySchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `https://deckprosnj.com${canonical}#business`,
    name: `DeckPros NJ — ${data.county} Deck Builder`,
    description: data.hero.description,
    url: `https://deckprosnj.com${canonical}`,
    telephone: '+1-201-657-0855',
    address: { '@type': 'PostalAddress', addressRegion: 'NJ', addressCountry: 'US' },
    areaServed: {
      '@type': 'AdministrativeArea',
      name: data.county,
      containedInPlace: { '@type': 'State', name: 'New Jersey' },
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '500',
      bestRating: '5',
    },
    priceRange: '$$',
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title={`Deck Builder in ${data.county}, NJ`}
        description={data.hero.description}
        canonical={canonical}
        keywords={`deck builder ${data.county} NJ, ${data.county} deck contractor, ${data.county} deck repair, ${data.county} composite decking, Trex installer ${data.county}, New Jersey deck builder`}
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Areas', url: '/areas' },
        { name: data.county, url: canonical },
      ]} />
      <SpeakableSchema />
      <FaqSchema faqs={faqs} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(countySchema)}</script>
      </Helmet>

      <Header />
      <main>
        {/* ─── Hero ─────────────────────────── */}
        <section className="relative pt-28 pb-14 bg-navy text-white overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <p className="section-eyebrow justify-center">Deck Contractor · {data.county}</p>
              <h1
                className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase leading-[1.05]"
                data-speakable
              >
                {data.hero.headline}
              </h1>
              <p
                className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed font-body mb-6"
                data-speakable
              >
                {data.hero.description}
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/contact">
                  <Button variant="cta" size="lg">
                    Get Free Estimate
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:2016570855">
                  <Button variant="hero" size="lg">
                    <Phone className="w-4 h-4" />
                    (201) 657-0855
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Breadcrumb ───────────────────── */}
        <nav aria-label="Breadcrumb" className="bg-cream-dark border-b-2 border-navy/10 py-3">
          <div className="container mx-auto px-4">
            <ol className="flex flex-wrap items-center gap-2 text-[11px] font-display font-bold uppercase tracking-[0.15em] text-stone">
              <li><Link to="/" className="hover:text-lime transition-colors">Home</Link></li>
              <li className="text-stone/50">/</li>
              <li><Link to="/areas" className="hover:text-lime transition-colors">Areas</Link></li>
              <li className="text-stone/50">/</li>
              <li className="text-lime">{data.county}</li>
            </ol>
          </div>
        </nav>

        {/* ─── About with Slideshow ──────────── */}
        <section className="section-compact bg-cream">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center max-w-7xl mx-auto">
              <Anim>
                <div>
                  <p className="section-eyebrow">About Our {data.county} Services</p>
                  <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-5 uppercase leading-tight">
                    {data.seoBlock.title}
                  </h2>
                  <div className="space-y-4 text-stone leading-relaxed font-body">
                    {data.seoBlock.paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-6 mb-6">
                    {[
                      'Licensed · Bonded · Insured',
                      'Lifetime Workmanship Warranty',
                      '25+ Years in New Jersey',
                      'Permits & HOA Handled',
                    ].map((item) => (
                      <div key={item} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-lime flex-shrink-0 mt-0.5" />
                        <span className="text-navy text-sm font-body font-medium">{item}</span>
                      </div>
                    ))}
                  </div>

                  <Link to="/contact">
                    <Button variant="cta" size="lg">
                      Get a Free {data.county.replace(' County', '')} Quote
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </Anim>

              <Anim delay={120}>
                <AutoSlideshow county={data.county} />
              </Anim>
            </div>
          </div>
        </section>

        {/* ─── Services Grid ──────────────── */}
        <section className="section-compact bg-cream-dark">
          <div className="container mx-auto px-4">
            <Anim>
              <div className="text-center mb-10 max-w-2xl mx-auto">
                <p className="section-eyebrow justify-center">Deck Services</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
                  Services in <span className="text-lime">{data.county}</span>
                </h2>
                <p className="text-stone font-body">
                  Every deck service — design, build, repair, replace, and beyond.
                </p>
              </div>
            </Anim>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
              {data.services.map((svc, i) => (
                <Anim key={svc.title} delay={i * 60}>
                  <div className="group bg-white border-2 border-navy/10 hover:border-lime hover:-translate-y-1 hover:shadow-hard transition-all duration-200 p-6 h-full">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-6 h-0.5 bg-lime" />
                      <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-lime">
                        Service {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="font-display text-base font-bold text-navy uppercase tracking-wide mb-2 group-hover:text-lime transition-colors">
                      {svc.title}
                    </h3>
                    <p className="text-stone text-sm leading-relaxed font-body">{svc.desc}</p>
                  </div>
                </Anim>
              ))}
            </div>

            <Anim>
              <div className="text-center mt-8">
                <Link to="/services">
                  <Button variant="cta" size="lg">
                    View All Services
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </Anim>
          </div>
        </section>

        {/* ─── Gallery ────────────────────── */}
        <section className="section-compact bg-cream">
          <div className="container mx-auto px-4">
            <Anim>
              <div className="text-center mb-10 max-w-2xl mx-auto">
                <p className="section-eyebrow justify-center">Recent Builds</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
                  Projects in <span className="text-lime">{data.county}</span>
                </h2>
              </div>
            </Anim>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-6xl mx-auto">
              {data.images.map((src, i) => (
                <Anim key={i} delay={i * 60}>
                  <div className="border-2 border-navy/10 hover:border-lime hover:-translate-y-1 hover:shadow-hard transition-all duration-200 overflow-hidden">
                    <img
                      src={src}
                      alt={data.imageAlts[i]}
                      className="w-full aspect-[4/3] object-cover transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                </Anim>
              ))}
            </div>
            <Anim>
              <div className="text-center mt-8">
                <Link to="/gallery">
                  <Button variant="outline" size="lg">
                    View Full Gallery
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </Anim>
          </div>
        </section>

        {/* ─── Towns We Serve ────────────── */}
        <section className="section-snug bg-cream-dark">
          <div className="container mx-auto px-4">
            <Anim>
              <div className="text-center mb-8 max-w-2xl mx-auto">
                <p className="section-eyebrow justify-center">Service Area</p>
                <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-navy">
                  Towns We Serve in <span className="text-lime">{data.county}</span>
                </h2>
              </div>
            </Anim>
            <Anim>
              <div className="flex flex-wrap justify-center gap-2 max-w-5xl mx-auto">
                {data.towns.map((town) => (
                  <Link
                    key={town}
                    to={`/areas/${town.toLowerCase().replace(/\s+/g, '-')}`}
                    className="flex items-center gap-2 bg-white border-2 border-navy text-navy hover:bg-lime hover:border-navy px-4 py-2.5 font-display uppercase text-xs font-bold tracking-[0.15em] transition-all"
                  >
                    <MapPin className="w-3 h-3" />
                    {town}
                  </Link>
                ))}
              </div>
            </Anim>
          </div>
        </section>

        {/* ─── Why Choose Us ─────────────── */}
        <section className="section-compact bg-navy text-white">
          <div className="container mx-auto px-4">
            <Anim>
              <div className="text-center mb-10 max-w-2xl mx-auto">
                <p className="section-eyebrow justify-center">Why DeckPros NJ</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold">
                  Trusted Deck Builder in <span className="text-lime">{data.county}</span>
                </h2>
              </div>
            </Anim>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
              {data.reasons.map((r, i) => (
                <Anim key={r.title} delay={i * 60}>
                  <div className="bg-navy-deep border-2 border-white/10 hover:border-lime transition-colors p-6 h-full">
                    <div className="w-11 h-11 bg-lime flex items-center justify-center border-2 border-navy mb-4">
                      <r.icon className="w-5 h-5 text-navy" />
                    </div>
                    <h3 className="font-display text-base font-bold text-white uppercase tracking-wide mb-2">
                      {r.title}
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed font-body">{r.desc}</p>
                  </div>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FAQ ──────────────────────── */}
        <section className="section-compact bg-cream">
          <div className="container mx-auto px-4">
            <Anim>
              <div className="text-center mb-10 max-w-2xl mx-auto">
                <p className="section-eyebrow justify-center">Common Questions</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
                  {data.county} Deck <span className="text-lime">FAQs</span>
                </h2>
              </div>
            </Anim>

            <div
              className="max-w-4xl mx-auto space-y-3"
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              {faqs.map((faq, i) => (
                <details
                  key={i}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  className="group bg-white border-2 border-navy/15 hover:border-lime transition-colors [&[open]]:border-lime [&[open]]:shadow-hard"
                >
                  <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
                    <h3
                      itemProp="name"
                      className="font-display text-sm md:text-base font-bold text-navy uppercase tracking-wide group-open:text-lime transition-colors"
                    >
                      {faq.question}
                    </h3>
                    <span className="font-display text-2xl text-lime leading-none transition-transform group-open:rotate-45 flex-shrink-0">+</span>
                  </summary>
                  <div
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    className="px-5 pb-5 -mt-1"
                  >
                    <p itemProp="text" className="text-stone text-sm leading-relaxed font-body border-t border-navy/10 pt-4">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
}

export { Hammer, Wrench, Layers, Home, Ruler, Palette, Shield, Award, Star, Phone };
